<template>
  <div class="mx-auto space-y-6 px-4 sm:px-6 lg:px-8 pb-8">
    <!-- Header -->
    <div class="max-w-4xl mx-auto bg-gradient-to-r from-primary-400 to-primary-600 text-white rounded-2xl shadow-xl p-4 sm:p-6 text-center">
      <h2 class="text-xl sm:text-2xl md:text-3xl font-extrabold tracking-wide flex items-center justify-center gap-2">
        <i class="pi pi-shopping-cart"></i> Shopping Cart
      </h2>
      <p class="mt-1 text-xs sm:text-sm text-white/80">
        {{ cartItems.length }} {{ cartItems.length === 1 ? 'item' : 'items' }} in your cart
      </p>
    </div>

    <!-- Empty Cart -->
    <div
      v-if="cartItems.length === 0"
      class="flex flex-col items-center justify-center py-16 sm:py-24 text-center bg-theme-card rounded-2xl border border-theme-border shadow-sm"
    >
      <div class="flex items-center justify-center w-24 h-24 rounded-full bg-theme-bg2">
        <i class="pi pi-shopping-cart text-5xl text-theme-muted"></i>
      </div>
      <h3 class="mt-5 text-xl sm:text-2xl font-bold text-theme-text">Your Cart is Empty</h3>
      <p class="mt-2 text-sm sm:text-base text-theme-muted max-w-sm">
        Add some items to your cart and get started with your rental journey.
      </p>
      <button
        @click="$router.push('/categories')"
        class="mt-5 inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm shadow-lg shadow-primary-600/20 transition-all active:scale-[0.98]"
      >
        <i class="pi pi-search text-sm"></i>
        Browse Categories
      </button>
    </div>

    <!-- Cart Content -->
    <template v-if="cartItems.length > 0">
      <!-- Desktop Table (lg+) -->
      <div class="hidden lg:block overflow-x-auto rounded-xl border border-theme-border bg-theme-card shadow-sm">
        <table class="min-w-full border-collapse">
          <thead>
            <tr class="bg-primary-600/10 dark:bg-primary-600/20 text-theme-text uppercase text-xs">
              <th class="py-3.5 px-4 text-left font-semibold border-b border-r border-theme-border">Item</th>
              <th class="py-3.5 px-4 text-center font-semibold border-b border-r border-theme-border">Price/Day</th>
              <th class="py-3.5 px-4 text-center font-semibold border-b border-r border-theme-border">Qty</th>
              <th class="py-3.5 px-4 text-center font-semibold border-b border-r border-theme-border">Start</th>
              <th class="py-3.5 px-4 text-center font-semibold border-b border-r border-theme-border">End</th>
              <th class="py-3.5 px-4 text-center font-semibold border-b border-r border-theme-border">Duration</th>
              <th class="py-3.5 px-4 text-right font-semibold border-b border-r border-theme-border">Total</th>
              <th class="py-3.5 px-4 text-center font-semibold border-b border-theme-border w-14"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, idx) in cartItems"
              :key="item.id"
              class="border-b border-theme-border transition-colors"
              :class="idx % 2 === 0 ? 'bg-theme-card' : 'bg-theme-bg2/50'"
            >
              <!-- Item info -->
              <td class="py-3 px-4 border-r border-theme-border">
                <div class="flex items-center gap-3">
                  <img
                    :src="getItemImage(item)"
                    :alt="getItemName(item)"
                    class="w-14 h-14 rounded-xl object-cover border border-theme-border shrink-0"
                  />
                  <button
                    @click="goToDetail(getItemId(item))"
                    class="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline text-left truncate max-w-[200px]"
                  >
                    {{ getItemName(item) }}
                  </button>
                </div>
              </td>

              <!-- Price -->
              <td class="py-3 px-4 text-center text-sm text-theme-text border-r border-theme-border">
                {{ formatPrice(getItemPrice(item)) }}
              </td>

              <!-- Quantity -->
              <td class="py-3 px-4 border-r border-theme-border">
                <div class="flex items-center justify-center gap-1">
                  <button
                    @click.prevent="adjustQuantity(item, -1)"
                    :disabled="item.quantity <= 1"
                    class="w-7 h-7 rounded-lg flex items-center justify-center text-theme-text2 hover:bg-theme-bg2 border border-theme-border transition disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <i class="pi pi-minus text-xs"></i>
                  </button>
                  <input
                    type="number"
                    v-model.number="item.quantity"
                    @input="debouncedHandleQuantityInput(item)"
                    class="w-12 text-center text-sm font-medium bg-theme-bg2 border border-theme-border rounded-lg py-1 text-theme-text focus:outline-none focus:ring-1 focus:ring-primary-600/40"
                    :min="1"
                    :max="getMaxQuantity(item)"
                  />
                  <button
                    @click.prevent="adjustQuantity(item, 1)"
                    class="w-7 h-7 rounded-lg flex items-center justify-center text-theme-text2 hover:bg-theme-bg2 border border-theme-border transition"
                  >
                    <i class="pi pi-plus text-xs"></i>
                  </button>
                </div>
              </td>

              <!-- Start Date -->
              <td class="py-3 px-4 text-center border-r border-theme-border">
                <input
                  type="date"
                  v-model="item.start_date"
                  @change="handleDateChange(item, 'start')"
                  class="text-xs bg-theme-bg2 border border-theme-border rounded-lg px-2 py-1.5 text-theme-text focus:outline-none focus:ring-1 focus:ring-primary-600/40"
                />
              </td>

              <!-- End Date -->
              <td class="py-3 px-4 text-center border-r border-theme-border">
                <input
                  type="date"
                  v-model="item.end_date"
                  @change="handleDateChange(item, 'end')"
                  class="text-xs bg-theme-bg2 border border-theme-border rounded-lg px-2 py-1.5 text-theme-text focus:outline-none focus:ring-1 focus:ring-primary-600/40"
                />
              </td>

              <!-- Duration -->
              <td class="py-3 px-4 text-center border-r border-theme-border">
                <span class="inline-flex items-center gap-1 text-sm text-theme-text2 bg-theme-bg2 px-2.5 py-1 rounded-full">
                  <i class="pi pi-calendar text-[10px]"></i>
                  {{ calculateLeaseDuration(item) }}d
                </span>
              </td>

              <!-- Total -->
              <td class="py-3 px-4 text-right text-sm font-bold text-theme-text border-r border-theme-border">
                {{ formatPrice(calculateItemTotal(item)) }}
              </td>

              <!-- Delete -->
              <td class="py-3 px-4 text-center">
                <button
                  @click.prevent="removeFromCart(item)"
                  class="w-8 h-8 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition"
                  aria-label="Remove item"
                >
                  <i class="pi pi-trash text-sm"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile/Tablet Cards (< lg) -->
      <div class="lg:hidden space-y-3">
        <div
          v-for="item in cartItems"
          :key="'mobile-' + item.id"
          class="bg-theme-card rounded-xl border border-theme-border shadow-sm overflow-hidden"
        >
          <!-- Top row: image + name + delete -->
          <div class="flex items-center gap-3 p-3 border-b border-theme-border">
            <img
              :src="getItemImage(item)"
              :alt="getItemName(item)"
              class="w-16 h-16 sm:w-20 sm:h-20 rounded-xl object-cover border border-theme-border shrink-0"
            />
            <div class="flex-1 min-w-0">
              <button
                @click="goToDetail(getItemId(item))"
                class="text-sm sm:text-base font-semibold text-primary-600 dark:text-primary-400 hover:underline text-left truncate block w-full"
              >
                {{ getItemName(item) }}
              </button>
              <p class="text-xs sm:text-sm text-theme-muted mt-0.5">
                {{ formatPrice(getItemPrice(item)) }} / day
              </p>
            </div>
            <button
              @click.prevent="removeFromCart(item)"
              class="w-9 h-9 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition shrink-0"
              aria-label="Remove item"
            >
              <i class="pi pi-trash"></i>
            </button>
          </div>

          <!-- Details grid -->
          <div class="p-3 space-y-3">
            <!-- Quantity row -->
            <div class="flex items-center justify-between">
              <span class="text-xs font-semibold text-theme-text2 uppercase">Quantity</span>
              <div class="flex items-center gap-1.5">
                <button
                  @click.prevent="adjustQuantity(item, -1)"
                  :disabled="item.quantity <= 1"
                  class="w-8 h-8 rounded-lg flex items-center justify-center text-theme-text2 hover:bg-theme-bg2 border border-theme-border transition disabled:opacity-30"
                >
                  <i class="pi pi-minus text-xs"></i>
                </button>
                <input
                  type="number"
                  v-model.number="item.quantity"
                  @input="debouncedHandleQuantityInput(item)"
                  class="w-14 text-center text-sm font-medium bg-theme-bg2 border border-theme-border rounded-lg py-1.5 text-theme-text focus:outline-none focus:ring-1 focus:ring-primary-600/40"
                  :min="1"
                  :max="getMaxQuantity(item)"
                />
                <button
                  @click.prevent="adjustQuantity(item, 1)"
                  class="w-8 h-8 rounded-lg flex items-center justify-center text-theme-text2 hover:bg-theme-bg2 border border-theme-border transition"
                >
                  <i class="pi pi-plus text-xs"></i>
                </button>
              </div>
            </div>

            <!-- Dates row -->
            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="block text-xs font-semibold text-theme-text2 uppercase mb-1">Start</label>
                <input
                  type="date"
                  v-model="item.start_date"
                  @change="handleDateChange(item, 'start')"
                  class="w-full text-xs sm:text-sm bg-theme-bg2 border border-theme-border rounded-lg px-2.5 py-2 text-theme-text focus:outline-none focus:ring-1 focus:ring-primary-600/40"
                />
              </div>
              <div>
                <label class="block text-xs font-semibold text-theme-text2 uppercase mb-1">End</label>
                <input
                  type="date"
                  v-model="item.end_date"
                  @change="handleDateChange(item, 'end')"
                  class="w-full text-xs sm:text-sm bg-theme-bg2 border border-theme-border rounded-lg px-2.5 py-2 text-theme-text focus:outline-none focus:ring-1 focus:ring-primary-600/40"
                />
              </div>
            </div>

            <!-- Duration + Total row -->
            <div class="flex items-center justify-between pt-2 border-t border-theme-border">
              <span class="inline-flex items-center gap-1 text-xs text-theme-muted bg-theme-bg2 px-2.5 py-1 rounded-full">
                <i class="pi pi-calendar text-[10px]"></i>
                {{ calculateLeaseDuration(item) }} days
              </span>
              <span class="text-base sm:text-lg font-bold text-theme-text">
                {{ formatPrice(calculateItemTotal(item)) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Cart Summary -->
      <div class="max-w-md ml-auto bg-theme-card rounded-xl border border-theme-border shadow-sm overflow-hidden">
        <div class="bg-primary-600/10 dark:bg-primary-600/20 px-5 py-3 border-b border-theme-border">
          <h3 class="text-sm font-bold text-theme-text uppercase tracking-wide">Cart Totals</h3>
        </div>
        <div class="p-5 space-y-3">
          <div class="flex justify-between text-sm">
            <span class="text-theme-text2">Subtotal</span>
            <span class="font-semibold text-theme-text">{{ formatPrice(subtotal) }}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-theme-text2">Service Fee (6%)</span>
            <span class="font-semibold text-theme-text">{{ formatPrice(serviceFee) }}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-theme-text2">Shipping</span>
            <span class="font-semibold text-theme-text">{{ formatPrice(0) }}</span>
          </div>
          <hr class="border-theme-border" />
          <div class="flex justify-between items-center">
            <span class="text-base font-bold text-theme-text">Total</span>
            <span class="text-xl font-extrabold text-primary-600">{{ formatPrice(subtotal + serviceFee) }}</span>
          </div>
        </div>
        <div class="px-5 pb-5">
          <button
            @click="guardCheckout"
            class="w-full py-3 rounded-xl font-bold text-base bg-primary-600 hover:bg-primary-500 text-white shadow-lg shadow-primary-600/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <i class="pi pi-lock text-sm"></i>
            Proceed to Checkout
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, watch, ref } from 'vue';
import { useCartStore } from '@/store/cart';
import { useAuthStore } from '@/store/auth';
import { useCurrency } from '@/composables/useCurrency';
import axios from 'axios';
import useNotifications from '@/store/notification.js';
import { useRouter } from 'vue-router';
import { useLogger } from '@/composables/useLogger';

const logger = useLogger('Cart');

const { showNotification } = useNotifications();
const cartStore = useCartStore();
const authStore = useAuthStore();
const router = useRouter();
const { formatPrice } = useCurrency();

const api_base_url = import.meta.env.VITE_API_BASE_URL;

// Use the store's cart directly: one row per CartItem
const cartItems = computed(() => cartStore.cart);

// Track previous valid dates per cart item so we can revert on error
const previousDates = ref({}); // { [itemId]: { start_date, end_date } }

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

// Helper: always get the equipment object (backend: item_details; guest: item)
const getEquipmentFromItem = (item) => {
  return item?.item_details || item?.item || null;
};

// Helper: max quantity allowed in UI (fallback high if we don't know)
const getMaxQuantity = (item) => {
  const eq = getEquipmentFromItem(item);
  return eq && typeof eq.available_quantity === 'number'
    ? eq.available_quantity
    : 999;
};

onMounted(async () => {
  await cartStore.loadCart();
  logger.debug('Cart loaded', { item_count: cartStore.cart.length });

  // Initialize previousDates from cart
  cartStore.cart.forEach((item) => {
    previousDates.value[item.id] = {
      start_date: item.start_date,
      end_date: item.end_date,
    };
  });
});

// Keep previousDates in sync as items change
watch(
  cartItems,
  (items) => {
    items.forEach((item) => {
      if (!previousDates.value[item.id]) {
        previousDates.value[item.id] = {
          start_date: item.start_date,
          end_date: item.end_date,
        };
      }
    });
  },
  { deep: true }
);

const removeFromCart = async (item) => {
  const name = getItemName(item);

  if (authStore.isAuthenticated) {
    cartStore.removeItem(item.id);
    try {
      await axios.delete(`${api_base_url}/api/cart-items/${item.id}/`, {
        withCredentials: true,
      });
      showNotification('Item Removed', `${name} has been removed from your cart.`, 'success');
    } catch (error) {
      showNotification('Error', `Error deleting ${name}.`, 'error');
    }
  } else {
    cartStore.removeItem(item.id);
  }
};

const adjustQuantity = async (item, adjustment) => {
  const newQuantity = item.quantity + adjustment;
  if (newQuantity <= 0) return;

  if (authStore.isAuthenticated) {
    const payload = {
      id: item.id,
      quantity: newQuantity,
      start_date: item.start_date,
      end_date: item.end_date,
    };

    try {
      await axios.put(`${api_base_url}/api/cart-items/${payload.id}/`, payload, {
        withCredentials: true,
      });
      cartStore.updateItemQuantity(item.id, newQuantity);
    } catch (error) {
      const name = getItemName(item);
      const backendMsg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        `You cannot add more of ${name} to the cart.`;
      showNotification('Error', backendMsg, 'error');
    }
  } else {
    cartStore.updateItemQuantity(item.id, newQuantity);
  }
};

const handleQuantityInput = async (item) => {
  const newQuantity = parseInt(item.quantity, 10);
  const maxQuantity = getMaxQuantity(item);

  if (Number.isNaN(newQuantity) || newQuantity <= 0) {
    item.quantity = 1;
    showNotification('Invalid Quantity', 'Please enter a valid quantity.', 'error');
    return;
  }

  if (newQuantity > maxQuantity) {
    item.quantity = maxQuantity;
    showNotification(
      'Invalid Quantity',
      `Please enter a valid quantity (max ${maxQuantity}).`,
      'error'
    );
    return;
  }

  if (authStore.isAuthenticated) {
    const payload = {
      id: item.id,
      quantity: newQuantity,
      start_date: item.start_date,
      end_date: item.end_date,
    };

    try {
      await axios.put(`${api_base_url}/api/cart-items/${item.id}/`, payload, {
        withCredentials: true,
      });
      cartStore.updateItemQuantity(item.id, newQuantity);
    } catch (error) {
      const name = getItemName(item);
      const backendMsg =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        `Error updating the quantity of ${name}.`;
      showNotification('Error', backendMsg, 'error');
    }
  } else {
    cartStore.updateItemQuantity(item.id, newQuantity);
  }
};

const debouncedHandleQuantityInput = debounce(handleQuantityInput, 700);

const handleDateChange = async (item, field) => {
  const prev = previousDates.value[item.id] || {
    start_date: item.start_date,
    end_date: item.end_date,
  };

  const newStart = item.start_date;
  const newEnd = item.end_date;

  if (!newStart || !newEnd) {
    showNotification('Invalid Dates', 'Both start and end dates are required.', 'error');
    item.start_date = prev.start_date;
    item.end_date = prev.end_date;
    return;
  }

  if (new Date(newStart) > new Date(newEnd)) {
    showNotification('Invalid Date Range', 'Start date cannot be after end date.', 'error');
    item.start_date = prev.start_date;
    item.end_date = prev.end_date;
    return;
  }

  // ==========================
  // 🧾 GUEST FLOW (no auth)
  // ==========================
  if (!authStore.isAuthenticated) {
    // Update dates on the edited item
    item.start_date = newStart;
    item.end_date = newEnd;

    const eq = getEquipmentFromItem(item);
    const equipmentId = eq?.id;

    if (equipmentId != null) {
      // Find OTHER guest cart items with same equipment + SAME date range
      const duplicates = cartStore.cart.filter((ci) => {
        if (ci.id === item.id) return false;
        const ciEq = getEquipmentFromItem(ci);
        if (!ciEq || ciEq.id == null) return false;
        return (
          ciEq.id === equipmentId &&
          ci.start_date === newStart &&
          ci.end_date === newEnd
        );
      });

      if (duplicates.length > 0) {
        // Merge quantities
        let mergedQty = item.quantity || 0;
        duplicates.forEach((ci) => {
          mergedQty += ci.quantity || 0;
        });

        // Update this line's quantity & total
        const price = getItemPrice(item);
        item.quantity = mergedQty;
        const leaseDuration = calculateLeaseDuration(item);
        item.total = price * mergedQty * leaseDuration;

        // Remove the duplicate lines from the guest cart
        duplicates.forEach((ci) => {
          const idx = cartStore.cart.findIndex((x) => x.id === ci.id);
          if (idx !== -1) {
            cartStore.cart.splice(idx, 1);
          }
        });
      }
    }

    // Persist to IndexedDB
    await cartStore.saveIndexedDBData(cartStore.cart);

    // Rebuild previousDates so it matches the updated cart structure
    previousDates.value = {};
    cartStore.cart.forEach((ci) => {
      previousDates.value[ci.id] = {
        start_date: ci.start_date,
        end_date: ci.end_date,
      };
    });

    showNotification('Dates Updated', 'Rental dates updated.', 'success');
    return;
  }

  // ==========================
  // 🔐 AUTHENTICATED FLOW
  // ==========================
  const payload = {
    id: item.id,
    quantity: item.quantity,
    start_date: newStart,
    end_date: newEnd,
  };

  try {
    await axios.put(`${api_base_url}/api/cart-items/${item.id}/`, payload, {
      withCredentials: true,
    });

    // Reload cart so backend merge logic reflects immediately
    await cartStore.loadCart();

    // Reset previousDates so it matches the new cart structure
    previousDates.value = {};
    cartStore.cart.forEach((ci) => {
      previousDates.value[ci.id] = {
        start_date: ci.start_date,
        end_date: ci.end_date,
      };
    });

    showNotification(
      'Dates Updated',
      'Rental dates have been updated successfully.',
      'success'
    );
  } catch (error) {
    const backendMsg =
      error?.response?.data?.error ||
      error?.response?.data?.detail ||
      'Could not update dates.';
    showNotification('Error', backendMsg, 'error');

    // Revert UI to previous dates
    item.start_date = prev.start_date;
    item.end_date = prev.end_date;
  }
};

const calculateLeaseDuration = (item) => {
  const startDate = new Date(item.start_date);
  const endDate = new Date(item.end_date);
  const timeDiff = endDate - startDate;
  // inclusive days: 10–12 → 3 days
  let days = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) + 1;
  return Math.max(days, 1);
};

const calculateItemTotal = (item) => {
  const price = getItemPrice(item);
  const leaseDuration = calculateLeaseDuration(item);
  return price * item.quantity * leaseDuration;
};

const subtotal = computed(() => {
  return cartItems.value.reduce((total, item) => {
    const price = getItemPrice(item);
    const leaseDuration = calculateLeaseDuration(item);
    return total + price * item.quantity * leaseDuration;
  }, 0);
});

const serviceFee = computed(() => {
  return subtotal.value * 0.06;
});

const getItemImage = (item) => {
  const eq = getEquipmentFromItem(item);

  if (eq && Array.isArray(eq.images) && eq.images.length > 0) {
    return eq.images[0].image_url;
  }

  // Fallback placeholder – change to your actual placeholder asset if needed
  return '/images/placeholder-equipment.png';
};

const getItemName = (item) => {
  const eq = getEquipmentFromItem(item);
  return eq?.name || 'Unknown';
};

const getItemPrice = (item) => {
  const eq = getEquipmentFromItem(item);
  return eq?.hourly_rate || 0;
};

const getItemId = (item) => {
  const eq = getEquipmentFromItem(item);
  return eq?.id || 0;
};

const goToDetail = (equipmentId) => {
  if (equipmentId) {
    router.push({ name: 'equipment-details', params: { id: equipmentId } });
  } else {
    showNotification('Item Error', 'Item ID is missing!', 'error');
  }
};

const guardCheckout = async () => {
  if (!authStore?.isAuthenticated) {
    showNotification('Please log in', 'Log in to continue to checkout.', 'info');
    return router.push({ name: 'login' });
  }

  const u = authStore?.user || {};

  if (u.role === 'lessee' && u.can_rent_items === false) {
    showNotification(
      'Documents not yet verified',
      'Your identity documents must be verified by our team before you can rent items.',
      'info'
    );
    try {
      return await router.push({ name: 'account' });
    } catch {
      return router.push('/new-profile/account');
    }
  }

  return router.push({ name: 'checkout' });
};

// Simple stub so the form submit handler exists
const submitCart = () => {
  // Optional: final client-side validations before checkout
};
</script>


<style scoped>
/* Hide number input spinners */
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type='number'] {
  -moz-appearance: textfield;
}
</style>
